import { ReactNode, useCallback, useEffect, useState, useRef } from 'react';
import {
  AgentCapability,
  AgentTier,
  supabase,
  requestStemSeparation,
} from '../../lib/supabase';
import { Track, TrackStatus } from '../../lib/types';
import { discoverAll, describeConnector, CONNECTORS } from '../../lib/connectors';
import { enqueueTask, describeTaskQueue, getWebhooks, describeWebhook, getTimeScaleLabel } from '../../lib/async-tasks';
import { getManifest, describeManifest, envelope, formatForAgent } from '../../lib/agent-protocol';
import {
  parseIntent,
  parseFunctionCall,
  decomposePlan,
  describePlan,
  memSet,
  memGet,
  memDump,
  memClear,
  formatIntrospection,
  getToolDefinitions,
  type ParsedIntent,
  type Plan,
} from '../../lib/agent-intelligence';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CircleDot,
  Eye,
  GitBranch,
  GitGraph,
  Lock,
  Network,
  Play,
  RefreshCw,
  Send,
  Square,
  Tag,
  Trash2,
  Upload,
  Zap,
} from 'lucide-react';
import { ViewKey } from '../../lib/view-keys';
import { ModelSelector, ModelSelectorInline } from './ModelSelector';
import { getStoredModelPref, setStoredModelPref, getModel, MODEL_REGISTRY } from '../../lib/model-registry';

type AgentState = 'idle' | 'planning' | 'executing' | 'awaiting_human' | 'observing' | 'blocked';

interface Stats {
  total: number;
  inReview: number;
  approved: number;
  durationMinutes: number;
}

interface AgentJob {
  id: string;
  owner_id: string;
  op: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  targets: string[];
  payload: Record<string, unknown>;
  result: Record<string, unknown>;
  error: string | null;
  progress: number;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

interface CapabilityDef {
  opcode: string;
  name: string;
  friendlyName: string;
  layer: AgentCapability;
  signature: string;
  description: string;
  consent: 'implicit' | 'confirm' | 'explicit';
  reversible: boolean;
  idempotent: boolean;
  icon: React.ReactNode;
  apply?: (track: Track) => Partial<Track>;
}

interface ThoughtEntry {
  id: string;
  ts: number;
  type: 'observation' | 'reasoning' | 'action' | 'result' | 'error' | 'handoff';
  content: string;
  detail?: string;
  meta?: string;
}

interface BreadcrumbEntry {
  id: string;
  ts: number;
  label: string;
  kind: 'select' | 'execute' | 'navigate' | 'config';
}

const CAPABILITIES: CapabilityDef[] = [
  {
    opcode: '0x10',
    name: 'catalog.batch_approve',
    friendlyName: 'Approve',
    layer: 'basic',
    signature: '(track_ids: uuid[]) -> Result<Track[]>',
    description: 'Mark selected tracks as approved.',
    consent: 'confirm',
    reversible: true,
    idempotent: true,
    icon: <Check size={12} />,
    apply: () => ({ status: 'approved' }),
  },
  {
    opcode: '0x11',
    name: 'catalog.batch_publish',
    friendlyName: 'Publish',
    layer: 'basic',
    signature: '(track_ids: uuid[]) -> Result<Track[]>',
    description: 'Promote approved tracks to published.',
    consent: 'explicit',
    reversible: true,
    idempotent: true,
    icon: <Zap size={12} />,
    apply: () => ({ status: 'published' }),
  },
  {
    opcode: '0x12',
    name: 'catalog.batch_review',
    friendlyName: 'Send to Review',
    layer: 'basic',
    signature: '(track_ids: uuid[]) -> Result<Track[]>',
    description: 'Send tracks back to review queue.',
    consent: 'implicit',
    reversible: true,
    idempotent: true,
    icon: <GitBranch size={12} />,
    apply: () => ({ status: 'in_review' }),
  },
  {
    opcode: '0x20',
    name: 'catalog.batch_retag',
    friendlyName: 'Tag',
    layer: 'advanced',
    signature: '(track_ids: uuid[], tag: string) -> Result<TagEvent[]>',
    description: 'Append a tag to track metadata.',
    consent: 'confirm',
    reversible: true,
    idempotent: false,
    icon: <Tag size={12} />,
  },
  {
    opcode: '0x21',
    name: 'alignment.bulk_solve',
    friendlyName: 'Align',
    layer: 'advanced',
    signature: '(track_ids: uuid[]) -> AsyncTask<TimestampDelta[]>',
    description: 'Word-level alignment. Long-running.',
    consent: 'confirm',
    reversible: false,
    idempotent: true,
    icon: <Network size={12} />,
  },
  {
    opcode: '0x22',
    name: 'stems.separate',
    friendlyName: 'Separate Stems',
    layer: 'advanced',
    signature: '(track_ids: uuid[], model?: htdemucs|htdemucs_6s) -> AsyncTask<StemJob>',
    description: 'Demucs HTDemucs stem separation via GPU inference. Splits audio into vocals, drums, bass, guitar, piano, other.',
    consent: 'confirm',
    reversible: false,
    idempotent: false,
    icon: <GitGraph size={12} />,
  },
  {
    opcode: '0x30',
    name: 'catalog.export',
    friendlyName: 'Export',
    layer: 'advanced',
    signature: '(track_ids: uuid[], format: csv|jsonl) -> File',
    description: 'Export selection as CSV or JSONL.',
    consent: 'implicit',
    reversible: true,
    idempotent: true,
    icon: <Upload size={12} />,
  },
  {
    opcode: '0xF0',
    name: 'catalog.batch_delete',
    friendlyName: 'Delete',
    layer: 'expert',
    signature: '(track_ids: uuid[]) -> Result<DeleteEvent[]>',
    description: 'Hard-delete. Irreversible.',
    consent: 'explicit',
    reversible: false,
    idempotent: true,
    icon: <Trash2 size={12} />,
  },
];

const CAPABILITY_RANK: Record<AgentCapability, number> = { basic: 0, advanced: 1, expert: 2 };

interface AgentControlPlaneProps {
  ownerId: string;
  tracks: Track[];
  stats: Stats;
  loading: boolean;
  tier: AgentTier;
  capability: AgentCapability;
  onTierChange: (t: AgentTier) => void;
  onCapabilityChange: (c: AgentCapability) => void;
  onNavigate: (v: ViewKey) => void;
  onSelectTrack: (id: string) => void;
  onRefresh: () => void;
  onTimerStart?: () => void;
}

export function AgentControlPlane({
  ownerId,
  tracks,
  stats,
  loading,
  tier,
  capability,
  onTierChange,
  onCapabilityChange,
  onNavigate,
  onSelectTrack,
  onRefresh,
  onTimerStart,
}: AgentControlPlaneProps) {
  const [jobs, setJobs] = useState<AgentJob[]>([]);
  const [pendingOp, setPendingOp] = useState<CapabilityDef | null>(null);
  const [agentState, setAgentState] = useState<AgentState>('idle');
  const [thoughts, setThoughts] = useState<ThoughtEntry[]>([]);
  const [commandInput, setCommandInput] = useState('');
  const [contextExpanded, setContextExpanded] = useState(false);
  const [selectedTargets, setSelectedTargets] = useState<string[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbEntry[]>([]);
  const [activePlan, setActivePlan] = useState<Plan | null>(null);
  const [stemModel, setStemModel] = useState(() => getStoredModelPref());
  const [showModelSelector, setShowModelSelector] = useState(false);
  const thoughtsEndRef = useRef<HTMLDivElement>(null);

  const isBasic = capability === 'basic';
  const isExpert = capability === 'expert';

  function pushBreadcrumb(label: string, kind: BreadcrumbEntry['kind']) {
    setBreadcrumbs((prev) => [...prev, { id: crypto.randomUUID(), ts: Date.now(), label, kind }].slice(-8));
  }

  const loadJobs = useCallback(async () => {
    const { data } = await supabase
      .from('agent_jobs')
      .select('*')
      .eq('owner_id', ownerId)
      .order('created_at', { ascending: false })
      .limit(20);
    setJobs((data ?? []) as AgentJob[]);
  }, [ownerId]);

  useEffect(() => {
    void loadJobs();
    const interval = window.setInterval(() => void loadJobs(), 4000);
    return () => window.clearInterval(interval);
  }, [loadJobs]);

  useEffect(() => {
    const running = jobs.filter((j) => j.status === 'running').length;
    if (pendingOp) setAgentState('awaiting_human');
    else if (running > 0) setAgentState('executing');
    else setAgentState('idle');
  }, [jobs, pendingOp]);

  useEffect(() => {
    thoughtsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thoughts]);

  const seededThoughtsRef = useRef(false);
  useEffect(() => {
    if (seededThoughtsRef.current || tracks.length === 0) return;
    seededThoughtsRef.current = true;
    if (isBasic) {
      addThought('observation', `Ready. ${stats.total} tracks loaded, ${stats.inReview} need review.`);
      if (stats.inReview > 0) {
        addThought('reasoning', `You have ${stats.inReview} tracks waiting. Try "select review" then "approve" to process them.`);
      }
    } else {
      addThought('observation', `Context loaded: ${tracks.length} tracks, ${stats.inReview} in_review, ${stats.approved} approved, ${stats.durationMinutes}min total.`, isExpert ? `owner=${ownerId.slice(0, 8)} | layer=${capability} | verbosity=${tier}` : undefined);
      if (stats.inReview > 0) {
        addThought('reasoning', `${stats.inReview} tracks in review queue. Suggest: inspect quality, then batch_approve or flag for changes.`);
      }
    }
  }, [tracks.length, isBasic, isExpert, ownerId, capability, tier, stats.total, stats.inReview, stats.approved, stats.durationMinutes]);

  function addThought(type: ThoughtEntry['type'], content: string, detail?: string, meta?: string) {
    setThoughts((prev) => [
      ...prev,
      { id: crypto.randomUUID(), ts: Date.now(), type, content, detail, meta },
    ].slice(-50));
  }

  function richError(err: { what: string; why: string; how: string; alts?: string[] }) {
    if (isBasic) {
      const guidance = err.how ? `\nTry this: ${err.how}` : '';
      const altStr = err.alts?.length ? `\nOr: ${err.alts.join(', ')}` : '';
      addThought('error', `${err.what}${guidance}${altStr}`);
    } else {
      const lines = [
        `[WHAT] ${err.what}`,
        `[WHY]  ${err.why}`,
        `[FIX]  ${err.how}`,
      ];
      if (err.alts?.length) lines.push(`[ALT]  ${err.alts.join(' | ')}`);
      addThought('error', lines.join('\n'), undefined, `error_ts=${Date.now()}`);
    }
  }

  const allowedCaps = CAPABILITIES.filter(
    (c) => CAPABILITY_RANK[c.layer] <= CAPABILITY_RANK[capability],
  );

  const lockedCaps = CAPABILITIES.filter(
    (c) => CAPABILITY_RANK[c.layer] > CAPABILITY_RANK[capability],
  );

  function parseCommand(input: string) {
    const trimmed = input.trim();
    if (!trimmed) return;

    // 1. Try function-call syntax first (JSON or shorthand)
    const fnCall = parseFunctionCall(trimmed);
    if (fnCall) {
      addThought('observation', `Function call: ${fnCall.name}(${JSON.stringify(fnCall.parameters)})`, `call_id=${fnCall.call_id}`);
      setAgentState('planning');
      executeFunctionCall(fnCall.name, fnCall.parameters);
      return;
    }

    // 2. Parse intent semantically
    const intent = parseIntent(trimmed);

    // 3. Show reasoning about what we understood
    if (!isBasic) {
      if (intent.confidence < 1) {
        addThought('observation', `Received: "${trimmed}"`, `intent=${intent.intent} | confidence=${(intent.confidence * 100).toFixed(0)}% | flags=[${intent.flags.join(',')}]${intent.pipe_to ? ` | pipe_to="${intent.pipe_to}"` : ''}${intent.is_compound ? ` | compound=${intent.sub_commands?.length}` : ''}`);
      } else {
        addThought('observation', `Received: "${trimmed}"`);
      }
    }
    setAgentState('planning');

    // 4. Handle compound commands (semicolons / "then")
    if (intent.is_compound && intent.sub_commands && intent.sub_commands.length > 1) {
      addThought('reasoning', `Compound command detected: ${intent.sub_commands.length} steps. Executing sequentially.`);
      let delay = 0;
      for (const sub of intent.sub_commands) {
        delay += 400;
        setTimeout(() => parseCommand(sub), delay);
      }
      return;
    }

    // 5. Handle pipe operator
    if (intent.pipe_to) {
      addThought('reasoning', `Pipeline: "${intent.raw.split('|')[0].trim()}" -> "${intent.pipe_to}"`);
      executeIntent(intent);
      setTimeout(() => parseCommand(intent.pipe_to!), 600);
      return;
    }

    // 6. Execute resolved intent
    executeIntent(intent);
  }

  function executeFunctionCall(name: string, params: Record<string, unknown>) {
    const intentMap: Record<string, string> = {
      'catalog.select': 'select',
      'catalog.approve': 'approve',
      'catalog.publish': 'publish',
      'catalog.review': 'review',
      'catalog.export': 'export',
      'catalog.tag': 'tag',
      'catalog.delete': 'delete',
      'catalog.align': 'align',
      'catalog.stems': 'separate',
      'stems.separate': 'separate',
      'system.status': 'status',
      'system.plan': 'plan',
      'system.memory_set': 'mem_set',
      'system.memory_get': 'mem_get',
      'system.introspect': 'introspect',
      'connector.discover': 'discover',
      'connector.describe': 'describe',
    };
    const intent = intentMap[name];
    if (intent) {
      const parsed: ParsedIntent = {
        raw: `${name}(${JSON.stringify(params)})`,
        intent,
        confidence: 1,
        params: params as Record<string, string | number | boolean>,
        flags: ['--fn-call'],
        is_compound: false,
      };
      executeIntent(parsed);
    } else {
      richError({
        what: `Unknown function: "${name}"`,
        why: 'This function name is not in the tool registry',
        how: 'Use "introspect" or "manifest --json" to see available tool definitions',
        alts: ['introspect', Object.keys(intentMap).slice(0, 3).join(', ')],
      });
      setAgentState('idle');
    }
  }

  function executeIntent(intent: ParsedIntent) {
    const wantsJson = intent.flags.includes('json');

    switch (intent.intent) {
      case 'status': {
        setTimeout(() => {
          const statusPayload = { total: stats.total, in_review: stats.inReview, approved: stats.approved, duration_minutes: stats.durationMinutes, jobs_active: jobs.filter(j => j.status === 'running').length, targets: selectedTargets.length };
          if (wantsJson || intent.params.json) {
            const env = envelope('status', statusPayload, { next: ['select review', 'approve', 'publish'] });
            addThought('result', formatForAgent(env, 'json'));
          } else if (isBasic) {
            addThought('result', `${stats.total} tracks total. ${stats.inReview} in review, ${stats.approved} approved. ${stats.durationMinutes} minutes of audio.`);
          } else {
            addThought('result',
              `corpus=${stats.total} | in_review=${stats.inReview} | approved=${stats.approved} | duration=${stats.durationMinutes}min | jobs_active=${jobs.filter(j => j.status === 'running').length}`,
              isExpert ? `layer=${capability} | tier=${tier} | targets=${selectedTargets.length}` : undefined,
              isExpert ? `envelope=${JSON.stringify(envelope('status', statusPayload, { next: ['select review', 'approve'] }))}` : undefined
            );
          }
          setAgentState('idle');
        }, 200);
        break;
      }

      case 'help': {
        if (isBasic) {
          const friendlyList = allowedCaps.map(c => `  ${c.friendlyName.toLowerCase()}`).join(', ');
          addThought('result', `Available: ${friendlyList}\n\nWorkflow: "select review" then "approve" or "publish".\nDiscovery: discover, describe <name>, connect <name>\nAsync: queue, webhooks\nPlanning: plan <goal>\nMemory: remember key=val, recall $key\nOther: status, open, context, help\n\nTip: Upgrade to "advanced" layer for more capabilities.`);
        } else {
          const capList = allowedCaps.map(c => `  ${c.opcode} ${c.name} [${c.consent}]${!c.reversible ? ' IRREVERSIBLE' : ''}`).join('\n');
          addThought('result', `Operations (layer=${capability}):\n${capList}\n\nInput modes: natural language | structured commands | function calls | pipelines\nCompound: "select review; approve; export" or "select review | approve"\nFunctions: catalog.select(filter=review) or {"name":"catalog.approve","parameters":{}}\nPlanning: plan <goal> | think <topic>\nMemory: remember key=val | recall $key | memory | clear memory\nIntrospection: introspect | manifest --json | tools\nAgent: manifest, .well-known, status --json\nAsync: queue, tasks, webhooks, webhook <id>`);
        }
        setAgentState('idle');
        break;
      }

      case 'select': {
        const filter = String(intent.params.filter || '').trim();
        let targets: Track[] = [];
        if (filter === 'all') targets = tracks;
        else if (filter === 'review' || filter === 'in_review') targets = tracks.filter(t => t.status === 'in_review');
        else if (filter === 'draft') targets = tracks.filter(t => t.status === 'draft');
        else if (filter === 'approved') targets = tracks.filter(t => t.status === 'approved');
        else if (filter === 'published') targets = tracks.filter(t => t.status === 'published');
        else targets = tracks.filter(t => t.title.toLowerCase().includes(filter) || t.artist.toLowerCase().includes(filter));

        setSelectedTargets(targets.map(t => t.id));
        memSet('last_select', { filter, count: targets.length }, { source: 'select' });
        pushBreadcrumb(`select ${filter || 'all'} (${targets.length})`, 'select');
        if (isBasic) {
          addThought('action', `Selected ${targets.length} track${targets.length !== 1 ? 's' : ''}.${targets.length > 0 ? ' Now run an action like "approve" or "publish".' : ''}`);
        } else {
          addThought('action', `Target set updated: ${targets.length} tracks.`, isExpert ? `filter="${filter || 'all'}" | ids=[${targets.slice(0, 3).map(t => t.id.slice(0, 6)).join(',')}${targets.length > 3 ? ',...' : ''}]` : undefined);
        }
        setAgentState('idle');
        break;
      }

      case 'approve':
      case 'publish':
      case 'review':
      case 'delete':
      case 'tag':
      case 'align':
      case 'export': {
        const opMap: Record<string, string> = {
          approve: 'batch_approve', publish: 'batch_publish', review: 'batch_review',
          delete: 'batch_delete', tag: 'batch_retag', align: 'bulk_solve', export: 'export',
          separate: 'stems.separate',
        };
        const capMatch = allowedCaps.find(c => c.name.includes(opMap[intent.intent] || intent.intent));
        if (!capMatch) {
          const lockedMatch = lockedCaps.find(c => c.name.includes(opMap[intent.intent] || intent.intent));
          if (lockedMatch) {
            richError({
              what: `"${lockedMatch.friendlyName}" is locked at layer=${lockedMatch.layer}`,
              why: `Current layer "${capability}" does not include this operation`,
              how: `Run "layer ${lockedMatch.layer}" to unlock`,
              alts: [`layer ${lockedMatch.layer}`, 'help'],
            });
          } else {
            richError({ what: `"${intent.intent}" isn't a recognized command`, why: 'Not found in capability registry', how: 'Type "help" to see what I can do', alts: ['help'] });
          }
          setAgentState('idle');
          return;
        }
        if (selectedTargets.length === 0) {
          richError({
            what: `Cannot ${capMatch.friendlyName.toLowerCase()}: no targets`,
            why: `"${capMatch.name}" requires a selection`,
            how: 'Select tracks first with "select review" or "select all"',
            alts: ['select review', 'select all'],
          });
          setAgentState('blocked');
          setTimeout(() => setAgentState('idle'), 1200);
          return;
        }
        if (!isBasic) {
          addThought('reasoning', `Planning: ${capMatch.name} on ${selectedTargets.length} targets. consent=${capMatch.consent}, reversible=${capMatch.reversible}`, isExpert ? `opcode=${capMatch.opcode} | idempotent=${capMatch.idempotent} | sig=${capMatch.signature}` : undefined);
        }
        void executeCapability(capMatch);
        break;
      }

      case 'open': {
        const targetId = selectedTargets[0];
        if (!targetId) {
          richError({ what: 'No tracks are selected', why: '"open" requires at least one track to be selected first', how: 'Run "select review" to pick tracks, then try again', alts: ['select review', 'select all'] });
        } else {
          onSelectTrack(targetId);
          onNavigate('review');
          addThought('action', isBasic ? 'Opening track in player.' : `Opening track ${targetId.slice(0, 8)} in review player.`);
        }
        setAgentState('idle');
        break;
      }

      case 'discover': {
        pushBreadcrumb('discover', 'navigate');
        if (isBasic) {
          const summary = CONNECTORS.map(c => `  ${c.name} (${c.status})`).join('\n');
          addThought('result', `Available API connectors:\n${summary}\n\nUse "describe <name>" for details.`);
        } else {
          addThought('result', discoverAll(), isExpert ? `registry_version=1.0 | total=${CONNECTORS.length}` : undefined);
        }
        setAgentState('idle');
        break;
      }

      case 'describe': {
        const target = String(intent.params.target || intent.params.id || '');
        const connector = CONNECTORS.find(c => c.id === target || c.name.toLowerCase().includes(target));
        if (connector) {
          pushBreadcrumb(`describe ${connector.id}`, 'navigate');
          addThought('result', isBasic
            ? `${connector.name}\n${connector.description}\n\nEndpoints:\n${connector.endpoints.map(e => `  ${e.name} - ${e.description}`).join('\n')}\n\nAuth: ${connector.auth.method}`
            : describeConnector(connector.id)
          );
        } else {
          richError({ what: `Couldn't find a connector called "${target}"`, why: 'No match in the connector registry', how: 'Run "discover" to see available connectors', alts: ['discover'] });
        }
        setAgentState('idle');
        break;
      }

      case 'connect': {
        const target = String(intent.params.target || '');
        const connector = CONNECTORS.find(c => c.id === target || c.name.toLowerCase().includes(target));
        if (connector) {
          pushBreadcrumb(`connect ${connector.id}`, 'config');
          addThought('handoff', `Connector ${connector.id} requires ${connector.auth.method} auth.\nStatus: ${connector.status}\nRef: ${connector.auth.docsUrl}`);
        } else {
          richError({ what: `Couldn't find a connector called "${target}"`, why: 'No match in registry', how: 'Run "discover" to see available connectors', alts: ['discover'] });
        }
        setAgentState('idle');
        break;
      }

      case 'queue': {
        pushBreadcrumb('queue', 'navigate');
        addThought('result', isBasic ? 'Task queue shows pending/running work. Use "webhooks" for endpoints.' : describeTaskQueue());
        setAgentState('idle');
        break;
      }

      case 'webhooks': {
        pushBreadcrumb('webhooks', 'navigate');
        const hooks = getWebhooks();
        const whId = String(intent.params.id || '');
        if (whId) {
          const desc = describeWebhook(whId);
          if (desc) addThought('result', desc);
          else richError({ what: `Webhook "${whId}" doesn't exist`, why: 'No matching webhook found', how: 'Run "webhooks" to see your active webhooks', alts: ['webhooks'] });
        } else {
          const list = hooks.map(w => `  ${w.id} [${w.active ? 'ACTIVE' : 'OFF'}] -> ${w.url}`).join('\n');
          addThought('result', `=== Webhook Registry ===\n${list}`);
        }
        setAgentState('idle');
        break;
      }

      case 'manifest': {
        pushBreadcrumb('manifest', 'navigate');
        if (wantsJson || intent.params.json) {
          addThought('result', JSON.stringify(getManifest(), null, 2));
        } else if (isBasic) {
          addThought('result', 'Use "manifest --json" for machine-readable spec.\nUse "tools" for function call definitions.\nUse "introspect" for full state.');
        } else {
          addThought('result', describeManifest());
        }
        setAgentState('idle');
        break;
      }

      case 'context': {
        setContextExpanded(true);
        addThought('result', isBasic ? `${tracks.length} tracks loaded. ${selectedTargets.length} selected.` : `Context expanded. corpus=${tracks.length} | targets=${selectedTargets.length} | layer=${capability}`);
        setAgentState('idle');
        break;
      }

      case 'model': {
        const modelArg = String(intent.params.filter || intent.params.model || '').trim();
        if (modelArg && modelArg !== 'list' && modelArg !== 'picker') {
          const resolved = getModel(modelArg);
          if (resolved) {
            setStemModel(resolved.id);
            setStoredModelPref(resolved.id);
            pushBreadcrumb(`model=${resolved.id}`, 'config');
            addThought('action', `Stem model set to ${resolved.name} (${resolved.architecture}).`,
              isExpert ? `id=${resolved.id} | params=${resolved.params_millions}M | rope=${resolved.rotary_embedding} | stems=[${resolved.stems.join(',')}]` : undefined);
          } else {
            addThought('result', `Unknown model "${modelArg}". Use "model list" to see available models or "model picker" to open selector.`);
          }
        } else if (modelArg === 'list') {
          const list = MODEL_REGISTRY.map(m => `  ${m.id} — ${m.name} [${m.architecture}] ${m.id === stemModel ? '(active)' : ''}`).join('\n');
          addThought('result', `Available transformer models:\n${list}\n\nUse "model <id>" to switch. Default: bs_roformer_v2 (RoFormer).`);
        } else {
          setShowModelSelector(true);
          addThought('action', 'Opening model picker...');
        }
        setAgentState('idle');
        break;
      }

      case 'layer': {
        const next = String(intent.params.level || '').trim();
        if (next === 'basic' || next === 'advanced' || next === 'expert') {
          onCapabilityChange(next);
          pushBreadcrumb(`layer=${next}`, 'config');
          addThought('action', `Layer changed to ${next}.`);
        } else {
          const newLayer = capability === 'basic' ? 'advanced' : capability === 'advanced' ? 'expert' : 'basic';
          onCapabilityChange(newLayer as AgentCapability);
          pushBreadcrumb(`layer=${newLayer}`, 'config');
          addThought('action', `Layer cycled to ${newLayer}.`);
        }
        setAgentState('idle');
        break;
      }

      case 'verbosity': {
        const next = String(intent.params.level || '');
        if (next === 'minimal' || next === 'standard' || next === 'verbose') {
          onTierChange(next);
          addThought('action', `Verbosity set to ${next}.`);
        } else {
          onTierChange(tier === 'minimal' ? 'standard' : tier === 'standard' ? 'verbose' : 'minimal');
          const newT = tier === 'minimal' ? 'standard' : tier === 'standard' ? 'verbose' : 'minimal';
          addThought('action', `Verbosity cycled to ${newT}.`);
        }
        setAgentState('idle');
        break;
      }

      case 'plan': {
        const goal = String(intent.params.goal || intent.raw.replace(/^plan\s*/i, ''));
        const plan = decomposePlan(goal, { trackCount: stats.total, inReview: stats.inReview, approved: stats.approved });
        setActivePlan(plan);
        memSet('active_plan', plan.id, { source: 'planner' });
        pushBreadcrumb(`plan: ${goal.slice(0, 20)}`, 'navigate');
        addThought('reasoning', `Decomposed goal into ${plan.steps.length}-step plan:`);
        addThought('result', describePlan(plan), isExpert ? `plan_id=${plan.id} | steps=${plan.steps.length} | deps=${plan.steps.filter(s => s.depends_on.length > 0).length}` : undefined);
        setAgentState('idle');
        break;
      }

      case 'think': {
        const about = String(intent.params.about || '');
        setAgentState('planning');
        addThought('reasoning', `Analyzing: ${about}`);
        setTimeout(() => {
          const reasoning = generateReasoning(about);
          addThought('reasoning', reasoning);
          setAgentState('idle');
        }, 400);
        break;
      }

      case 'mem_set': {
        const key = String(intent.params.key || '');
        const value = String(intent.params.value || '');
        if (key && value) {
          memSet(key, value, { source: 'user' });
          addThought('action', `Memory: ${key} = "${value}"`);
        }
        setAgentState('idle');
        break;
      }

      case 'mem_get': {
        const key = String(intent.params.key || '');
        const val = memGet(key);
        if (val !== undefined) {
          addThought('result', `$${key} = ${JSON.stringify(val)}`);
        } else {
          addThought('result', `$${key} is not set.`);
        }
        setAgentState('idle');
        break;
      }

      case 'mem_dump': {
        const slots = memDump();
        if (slots.length === 0) {
          addThought('result', 'Working memory is clear — it will fill as you work.');
        } else {
          const lines = slots.map(s => `  $${s.key} = ${JSON.stringify(s.value)} [${s.type}] (${s.source})`).join('\n');
          addThought('result', `Working Memory (${slots.length} slots):\n${lines}`);
        }
        setAgentState('idle');
        break;
      }

      case 'mem_clear': {
        memClear();
        addThought('action', 'Working memory cleared.');
        setAgentState('idle');
        break;
      }

      case 'introspect': {
        const data = {
          agent_id: 'mediablox-pipes',
          layer: capability,
          tier,
          state: agentState,
          targets_count: selectedTargets.length,
          memory_slots: memDump().length,
          active_plan: activePlan,
          breadcrumb_depth: breadcrumbs.length,
          available_ops: allowedCaps.map(c => c.name),
          tool_definitions_count: getToolDefinitions().length,
          supported_protocols: ['mediablox-agent/1.0', 'openclaw/1.x', 'hermes/2.x'],
          input_modes: ['natural_language', 'structured_command', 'function_call', 'pipeline', 'compound'],
        };
        if (wantsJson) {
          addThought('result', JSON.stringify(data, null, 2));
        } else {
          addThought('result', formatIntrospection(data));
        }
        setAgentState('idle');
        break;
      }

      // --- Expert workflow intents ---

      case 'audit': {
        const scope = String(intent.params.scope || 'all');
        pushBreadcrumb(`audit ${scope}`, 'navigate');
        const auditLines = [
          `=== Audit Log (scope: ${scope}) ===`,
          `Breadcrumbs: ${breadcrumbs.length} operations recorded`,
          ...breadcrumbs.slice(-10).map(b => `  [${new Date(b.ts).toISOString().slice(11, 19)}] ${b.kind}: ${b.label}`),
          ``,
          `Working memory: ${memDump().length} slots`,
          `Targets modified this session: ${selectedTargets.length}`,
          `Plan state: ${activePlan ? activePlan.state : 'none'}`,
        ];
        addThought('result', auditLines.join('\n'));
        setAgentState('idle');
        break;
      }

      case 'evaluate': {
        const criteria = String(intent.params.criteria || '');
        setAgentState('planning');
        addThought('reasoning', `Evaluating against criteria: "${criteria}"`);
        setTimeout(() => {
          const evalResult = [
            `=== Evaluation Report ===`,
            `Criteria: ${criteria}`,
            `Corpus size: ${stats.total} tracks`,
            ``,
            `Readiness:`,
            `  Tracks in review: ${stats.inReview} (${stats.total > 0 ? ((stats.inReview / stats.total) * 100).toFixed(0) : 0}% of corpus)`,
            `  Tracks approved: ${stats.approved} (${stats.total > 0 ? ((stats.approved / stats.total) * 100).toFixed(0) : 0}% ready)`,
            `  Duration coverage: ${stats.durationMinutes} minutes`,
            ``,
            `Assessment: ${stats.inReview === 0 ? 'PASS - No pending reviews' : stats.inReview <= 3 ? 'WARN - Minor review backlog' : 'FAIL - Significant review backlog'}`,
            `Recommendation: ${stats.inReview > 0 ? `Process ${stats.inReview} review items before release` : 'Corpus is release-ready'}`,
          ];
          addThought('result', evalResult.join('\n'), isExpert ? `eval_score=${stats.inReview === 0 ? 1.0 : Math.max(0, 1 - stats.inReview / stats.total).toFixed(2)} | pass=${stats.inReview === 0}` : undefined);
          setAgentState('idle');
        }, 400);
        break;
      }

      case 'hypothesis': {
        const statement = String(intent.params.statement || '');
        addThought('reasoning', `Testing hypothesis: "${statement}"`);
        setTimeout(() => {
          const evidence: string[] = [];
          if (statement.toLowerCase().includes('ready') || statement.toLowerCase().includes('release')) {
            evidence.push(`Evidence FOR: ${stats.approved} tracks approved`);
            evidence.push(`Evidence AGAINST: ${stats.inReview} tracks still in review`);
            evidence.push(`Verdict: ${stats.inReview === 0 ? 'SUPPORTED' : 'REFUTED'} (review queue non-empty)`);
          } else if (statement.toLowerCase().includes('quality') || statement.toLowerCase().includes('good')) {
            evidence.push(`Evidence: ${stats.approved} tracks passed review (human-approved)`);
            evidence.push(`Coverage: ${stats.durationMinutes} minutes of verified audio`);
            evidence.push(`Verdict: PARTIALLY SUPPORTED (quality is subjective, approval is signal)`);
          } else {
            evidence.push(`Insufficient data to evaluate hypothesis directly`);
            evidence.push(`Suggest: provide measurable criteria via "evaluate <criteria>"`);
            evidence.push(`Verdict: INDETERMINATE`);
          }
          addThought('result', `Hypothesis: ${statement}\n\n${evidence.join('\n')}`);
          setAgentState('idle');
        }, 500);
        break;
      }

      case 'batch': {
        const operations = String(intent.params.operations || '');
        addThought('reasoning', `Batch orchestration: parsing "${operations}" into parallel operations`);
        const ops = operations.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
        if (ops.length === 0) {
          addThought('result', 'Batch orchestration:\n  Usage: batch <op1>, <op2>, <op3>\n  Example: batch "select review, approve, export csv"\n  Ops run sequentially unless forked with "fork"');
        } else {
          addThought('result', `Batch plan: ${ops.length} operations queued\n${ops.map((o, i) => `  [${i + 1}] ${o}`).join('\n')}\n\nExecuting sequentially...`);
          let delay = 0;
          for (const op of ops) {
            delay += 500;
            setTimeout(() => parseCommand(op), delay);
          }
        }
        setAgentState('idle');
        break;
      }

      case 'schedule': {
        const spec = String(intent.params.spec || '');
        pushBreadcrumb(`schedule: ${spec.slice(0, 20)}`, 'config');
        addThought('result', [
          `=== Temporal Scheduler ===`,
          `Spec: ${spec}`,
          `Status: registered (simulated)`,
          ``,
          `Scheduling is available for:`,
          `  - Periodic exports (e.g., "every 6h export csv")`,
          `  - Review reminders (e.g., "every day at 9am status")`,
          `  - Connector syncs (e.g., "every 30m sync spotify")`,
          `  - Batch publishing (e.g., "at 2pm publish approved")`,
          ``,
          `Note: Schedules trigger via the async queue + webhook system.`,
        ].join('\n'));
        setAgentState('idle');
        break;
      }

      case 'dag': {
        const expr = String(intent.params.expr || '');
        if (activePlan) {
          const depGraph = activePlan.steps.map(s => {
            const deps = s.depends_on.length > 0 ? ` <- [${s.depends_on.join(', ')}]` : ' (root)';
            return `  ${s.id}: ${s.action}${deps} [${s.state}]`;
          });
          addThought('result', `=== DAG: ${activePlan.goal} ===\n${depGraph.join('\n')}\n\nCritical path: ${activePlan.steps.filter(s => s.depends_on.length > 0).length} dependent nodes\nParallelizable: ${activePlan.steps.filter(s => s.depends_on.length === 0).length} root nodes`);
        } else if (expr) {
          addThought('result', `No active plan. Create one with "plan ${expr}" to generate a DAG.`);
        } else {
          addThought('result', 'No active DAG. Use "plan <goal>" to generate a dependency graph, then "dag" to visualize it.');
        }
        setAgentState('idle');
        break;
      }

      case 'trace': {
        const target = String(intent.params.target || '');
        pushBreadcrumb(`trace ${target}`, 'navigate');
        const relevantCrumbs = breadcrumbs.filter(b => b.label.toLowerCase().includes(target.toLowerCase()));
        if (relevantCrumbs.length > 0) {
          const lines = relevantCrumbs.map(b => `  ${new Date(b.ts).toISOString().slice(11, 19)} [${b.kind}] ${b.label}`);
          addThought('result', `=== Lineage Trace: "${target}" ===\n${lines.join('\n')}\n\nTotal operations: ${relevantCrumbs.length}`);
        } else {
          addThought('result', `No lineage recorded for "${target}" yet. Run some operations first, then lineage will show the full history of changes.`);
        }
        setAgentState('idle');
        break;
      }

      case 'policy': {
        addThought('result', [
          `=== Policy Engine ===`,
          `Active policies:`,
          `  [RLS] Row-level security on all tables (owner_id = auth.uid())`,
          `  [CONSENT] Destructive ops (delete, publish) require explicit confirmation`,
          `  [LAYER] Operations gated by capability layer (basic/advanced/expert)`,
          `  [AUDIT] All state mutations logged to breadcrumb trail`,
          `  [RATE] 60 commands/min, 10 concurrent async tasks`,
          ``,
          `Policy violations this session: 0`,
          `Escalation path: blocked ops -> richError -> suggest unlock`,
        ].join('\n'));
        setAgentState('idle');
        break;
      }

      case 'stream': {
        const channel = String(intent.params.channel || 'all');
        addThought('result', [
          `=== Event Stream (channel: ${channel}) ===`,
          `Stream protocol: SSE (Server-Sent Events)`,
          ``,
          `Available channels:`,
          `  all         - All events`,
          `  mutations   - Track status changes`,
          `  jobs        - Async task lifecycle (queued/running/done/failed)`,
          `  webhooks    - Outbound delivery events`,
          `  swarm       - Agent join/leave/heartbeat`,
          `  plan        - Plan step transitions`,
          ``,
          `Last 3 events:`,
          ...breadcrumbs.slice(-3).map(b => `  ${b.kind}:${b.label}`),
          ``,
          `To subscribe: connect to /api/events?channel=${channel}`,
        ].join('\n'));
        setAgentState('idle');
        break;
      }

      case 'cot': {
        const cotEntries = thoughts.filter(t => t.type === 'reasoning' || t.type === 'observation');
        if (cotEntries.length === 0) {
          addThought('result', 'No reasoning traces yet. Start running commands and reasoning will appear here.');
        } else {
          const lines = cotEntries.slice(-8).map(t => `  [${t.type}] ${t.content.slice(0, 100)}${t.content.length > 100 ? '...' : ''}`);
          addThought('result', `=== Chain of Thought (last ${lines.length} entries) ===\n${lines.join('\n')}\n\nTotal reasoning steps: ${cotEntries.length}`);
        }
        setAgentState('idle');
        break;
      }

      case 'delegate': {
        const task = String(intent.params.task || '');
        const agent = String(intent.params.agent || '');
        pushBreadcrumb(`delegate "${task}" -> ${agent}`, 'execute');
        addThought('action', `Delegating "${task}" to swarm node: ${agent}`);
        addThought('result', [
          `Task dispatched:`,
          `  Task: ${task}`,
          `  Target: ${agent}`,
          `  Status: queued (simulated)`,
          `  Protocol: swarm.delegate/1.0`,
          ``,
          `The target agent will execute independently and report back via event stream.`,
          `Monitor with: stream swarm`,
        ].join('\n'));
        setAgentState('idle');
        break;
      }

      case 'fork': {
        const tasks = String(intent.params.tasks || '').split(/[,;]+/).map(s => s.trim()).filter(Boolean);
        if (tasks.length < 2) {
          addThought('result', 'Fork requires 2+ tasks.\n  Usage: fork <task1>, <task2>\n  Example: fork "select review, select approved"');
        } else {
          pushBreadcrumb(`fork ${tasks.length} tasks`, 'execute');
          addThought('action', `Forking ${tasks.length} parallel executions:`);
          addThought('result', tasks.map((t, i) => `  [thread_${i}] ${t}`).join('\n') + '\n\nAll threads executing concurrently. Use "join" to await completion.');
          for (const t of tasks) {
            setTimeout(() => parseCommand(t), 300);
          }
        }
        setAgentState('idle');
        break;
      }

      case 'join': {
        addThought('result', 'All forked threads have resolved. Barrier cleared.\nUse "status" to see updated state.');
        setAgentState('idle');
        break;
      }

      case 'checkpoint': {
        const snapshot = {
          ts: Date.now(),
          targets: selectedTargets.length,
          memory: memDump().length,
          breadcrumbs: breadcrumbs.length,
          plan: activePlan?.id || null,
          stats: { ...stats },
        };
        memSet('_checkpoint', snapshot, { source: 'system' });
        pushBreadcrumb('checkpoint', 'config');
        addThought('action', `Checkpoint saved. State snapshot stored in $._checkpoint\n  Targets: ${snapshot.targets} | Memory: ${snapshot.memory} slots | Breadcrumbs: ${snapshot.breadcrumbs}`);
        setAgentState('idle');
        break;
      }

      case 'rollback': {
        const target = String(intent.params.to || 'last');
        const checkpoint = memGet('_checkpoint');
        if (checkpoint && target === 'last') {
          addThought('action', `Rolling back to last checkpoint. Clearing transient state.`);
          setSelectedTargets([]);
          setActivePlan(null);
          addThought('result', 'Rollback complete. Targets cleared, plan reset. Memory checkpoint preserved.');
        } else {
          addThought('result', `No checkpoint saved. Run "checkpoint" to create a save point you can roll back to.`);
        }
        setAgentState('idle');
        break;
      }

      case 'compare': {
        const a = String(intent.params.a || '');
        const b = String(intent.params.b || '');
        addThought('reasoning', `Comparing: "${a}" vs "${b}"`);
        addThought('result', [
          `=== Comparison: ${a} vs ${b} ===`,
          ``,
          `This operation compares two states, selections, or outputs.`,
          `For track comparisons: select each set and use "status --json" to capture metrics.`,
          `For temporal comparison: use "checkpoint" before changes, then compare with post-state.`,
          ``,
          `Structured diff available via: compare --json`,
        ].join('\n'));
        setAgentState('idle');
        break;
      }

      case 'unknown':
      default: {
        // Try matching against capabilities by name (fallback)
        const raw = intent.raw.toLowerCase().trim();
        const capMatch = allowedCaps.find(c => {
          const shortName = c.name.split('.').pop() || '';
          const friendly = c.friendlyName.toLowerCase();
          return raw === shortName || raw === friendly || raw.startsWith(shortName + ' ') || raw === c.name;
        });
        if (capMatch) {
          if (selectedTargets.length === 0) {
            richError({ what: `Can't ${capMatch.friendlyName.toLowerCase()} — no tracks selected`, why: 'This operation needs at least one track selected', how: 'Run "select review" to choose tracks first', alts: ['select review'] });
            setAgentState('idle');
          } else {
            void executeCapability(capMatch);
          }
          return;
        }

        // Check if it's a "tools" request
        if (raw === 'tools' || raw === 'functions' || raw === 'tool_definitions') {
          const defs = getToolDefinitions();
          if (wantsJson) {
            addThought('result', JSON.stringify(defs, null, 2));
          } else {
            const lines = defs.map(d => `  ${d.name}(${d.parameters.required.join(', ')}) - ${d.description}`).join('\n');
            addThought('result', `Tool Definitions (${defs.length}):\n${lines}\n\nCall with: {"name":"tool.name","parameters":{...}}\nOr shorthand: tool.name(param=value)`);
          }
          setAgentState('idle');
          return;
        }

        // Confidence-based suggestion
        if (intent.confidence > 0.5) {
          addThought('reasoning', `Low confidence match: "${intent.intent}" (${(intent.confidence * 100).toFixed(0)}%). Attempting execution.`);
          const retryIntent = { ...intent, intent: intent.intent, confidence: 1 };
          executeIntent(retryIntent);
        } else {
          richError({
            what: `Could not resolve: "${intent.raw}"`,
            why: `No intent matched (best: ${intent.intent} at ${(intent.confidence * 100).toFixed(0)}%)`,
            how: 'Try natural language, structured commands, or function calls',
            alts: ['help', 'introspect', 'tools', 'plan <goal>'],
          });
          setAgentState('idle');
        }
        break;
      }
    }
  }

  function generateReasoning(topic: string): string {
    const lowerTopic = topic.toLowerCase();
    if (lowerTopic.includes('review') || lowerTopic.includes('quality')) {
      return `Analysis: ${stats.inReview} tracks in review. Suggest batch inspection by selecting review queue, then approve tracks that meet quality bar. Consider exporting first for offline audit.`;
    }
    if (lowerTopic.includes('publish') || lowerTopic.includes('release')) {
      return `Analysis: ${stats.approved} tracks ready for publication. Publishing is async (15-60s) and triggers webhook delivery to distribution endpoints. Recommend: verify approved set, then publish.`;
    }
    if (lowerTopic.includes('connector') || lowerTopic.includes('integrat')) {
      return `Analysis: ${CONNECTORS.length} connectors available. ${CONNECTORS.filter(c => c.status === 'configured' || c.status === 'connected').length} ready, ${CONNECTORS.filter(c => c.status === 'stub').length} stubs. Use "discover" then "describe <id>" to evaluate.`;
    }
    return `Reasoning about "${topic}": Current state has ${stats.total} tracks (${stats.inReview} review, ${stats.approved} approved). ${selectedTargets.length} targets selected. Layer=${capability}. Consider what operation best advances this goal.`;
  }

  async function executeCapability(cap: CapabilityDef, force = false) {
    if ((cap.consent === 'explicit' || cap.consent === 'confirm') && !force) {
      setAgentState('awaiting_human');
      onTimerStart?.();
      if (isBasic) {
        addThought('handoff', `I need your confirmation to ${cap.friendlyName.toLowerCase()} ${selectedTargets.length} track${selectedTargets.length !== 1 ? 's' : ''}.`);
      } else {
        addThought('handoff', `Consent required (${cap.consent}). Awaiting confirmation: ${cap.name} on ${selectedTargets.length} targets.`);
      }
      setPendingOp(cap);
      return;
    }

    setAgentState('executing');
    onTimerStart?.();
    pushBreadcrumb(`${cap.friendlyName.toLowerCase()} (${selectedTargets.length})`, 'execute');
    const asyncTask = enqueueTask(cap.name, selectedTargets);
    if (isBasic) {
      const timeLabel = getTimeScaleLabel(asyncTask.time_scale);
      addThought('action', `${cap.friendlyName}ing ${selectedTargets.length} track${selectedTargets.length !== 1 ? 's' : ''}...${asyncTask.time_scale !== 'instant' ? ` (est. ${timeLabel})` : ''}`);
    } else {
      addThought('action', `Executing: ${cap.name} on ${selectedTargets.length} targets...`, isExpert ? `job_id=pending | opcode=${cap.opcode} | time_scale=${asyncTask.time_scale} | task=${asyncTask.id}` : undefined);
    }

    const targets = selectedTargets;
    const { data: jobInsert } = await supabase
      .from('agent_jobs')
      .insert({
        owner_id: ownerId,
        op: cap.name,
        status: 'running',
        targets,
        payload: { tier, capability },
        progress: 5,
        started_at: new Date().toISOString(),
      })
      .select('*')
      .maybeSingle();

    try {
      if (cap.apply) {
        const patch = { ...cap.apply(tracks[0]), updated_at: new Date().toISOString() };
        const { error } = await supabase.from('tracks').update(patch).in('id', targets).eq('owner_id', ownerId);
        if (error) throw new Error(error.message);
      } else if (cap.name === 'catalog.batch_retag') {
        const tag = window.prompt('Enter tag name:')?.trim();
        if (!tag) throw new Error('Tag value required');
        const rows = targets.map((id) => ({ track_id: id, owner_id: ownerId, field_name: `tag.${tag}`, field_value: 'true' }));
        const { error } = await supabase.from('track_metadata').insert(rows);
        if (error) throw new Error(error.message);
      } else if (cap.name === 'catalog.export') {
        const subset = tracks.filter((t) => targets.includes(t.id));
        downloadFile(`pipes-export-${Date.now()}.csv`, toCsv(subset));
      } else if (cap.name === 'catalog.batch_delete') {
        const { error } = await supabase.from('tracks').delete().in('id', targets).eq('owner_id', ownerId);
        if (error) throw new Error(error.message);
      } else if (cap.name === 'stems.separate') {
        const model = stemModel;
        const modelInfo = getModel(model);
        addThought('reasoning', `Model: ${modelInfo?.name ?? model} (${modelInfo?.architecture ?? 'unknown'}) | ${modelInfo?.stems.length ?? '?'} stems | RoPE=${modelInfo?.rotary_embedding ?? false}`,
          isExpert ? `model_id=${model} | params=${modelInfo?.params_millions ?? '?'}M | speed=${modelInfo?.inference_speed ?? '?'} | vram=${modelInfo?.memory_gb ?? '?'}GB` : undefined);
        const results: { track_id: string; job_id: string; status: string }[] = [];
        for (const trackId of targets) {
          const resp = await supabase.functions.invoke('demucs-separate', {
            body: { track_id: trackId, owner_id: ownerId, model },
          });
          if (resp.data && !resp.error) {
            results.push({ track_id: trackId, job_id: resp.data.job_id, status: resp.data.status });
          } else {
            results.push({ track_id: trackId, job_id: '', status: 'fallback' });
            await requestStemSeparation(ownerId, trackId, model as import('../../lib/supabase').StemModelPref);
          }
        }
        addThought('observation', `Stem separation dispatched for ${results.length} track${results.length !== 1 ? 's' : ''}. Model: ${modelInfo?.name ?? model}. Backend: ${results[0]?.status === 'processing' ? 'GPU inference (Replicate)' : 'completed (stub)'}. Jobs: ${results.map(r => r.job_id.slice(0, 8)).join(', ')}`);
      }

      if (jobInsert) {
        await supabase.from('agent_jobs').update({ status: 'succeeded', progress: 100, completed_at: new Date().toISOString(), result: { succeeded: targets.length } }).eq('id', jobInsert.id);
      }
      if (isBasic) {
        addThought('result', `Done! ${targets.length} track${targets.length !== 1 ? 's' : ''} ${cap.friendlyName.toLowerCase()}ed.`);
      } else {
        addThought('result', `Completed: ${cap.name} succeeded on ${targets.length} targets.`, isExpert && jobInsert ? `job_id=${jobInsert.id.slice(0, 8)} | duration=${Date.now() - new Date(jobInsert.started_at!).getTime()}ms` : undefined);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (jobInsert) {
        await supabase.from('agent_jobs').update({ status: 'failed', completed_at: new Date().toISOString(), error: msg }).eq('id', jobInsert.id);
      }
      richError({
        what: `Something went wrong while running this operation`,
        why: msg,
        how: 'Check the track data and try again, or run "status" to see what happened',
        alts: ['status', 'select review'],
      });
    }

    setPendingOp(null);
    setSelectedTargets([]);
    setAgentState('observing');
    void loadJobs();
    onRefresh();
    setTimeout(() => setAgentState('idle'), 800);
  }

  function handleCommandSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!commandInput.trim()) return;
    parseCommand(commandInput);
    setCommandInput('');
  }

  const runningCount = jobs.filter(j => j.status === 'running').length;
  const recentJobs = jobs.slice(0, isBasic ? 3 : 8);

  return (
    <div className="agent-mode fade-in -mx-4 -my-4 md:-mx-8 md:-my-6 agent-pinstripe h-[calc(100vh-104px)] flex flex-col overflow-hidden">
      {/* Identity bar -- density scales with layer */}
      <div className="px-5 py-2.5 border-b border-white/[0.08] flex items-center justify-between gap-4 bg-[#060a12]">
        <div className="flex items-center gap-4">
          <AgentStatePill state={agentState} simple={isBasic} />
          {!isBasic && (
            <div className="hidden md:flex items-center gap-3 text-[10px] font-mono text-slate-400">
              <span>layer=<span className="text-slate-200">{capability}</span></span>
              <span>verbosity=<span className="text-slate-200">{tier}</span></span>
              <span>targets=<span className={selectedTargets.length > 0 ? 'text-blue-200' : 'text-slate-200'}>{selectedTargets.length}</span></span>
              <span>corpus=<span className="text-slate-200">{stats.total}</span></span>
            </div>
          )}
          {isBasic && (
            <div className="hidden md:flex items-center gap-2 text-[11px] text-slate-300">
              {selectedTargets.length > 0 && <span className="text-blue-200 font-medium">{selectedTargets.length} selected</span>}
              {selectedTargets.length === 0 && <span>{stats.total} tracks</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <LayerSwitcher capability={capability} onChange={onCapabilityChange} />
          {!isBasic && (
            <button onClick={() => { onTierChange(tier === 'minimal' ? 'standard' : tier === 'standard' ? 'verbose' : 'minimal'); }} className="text-[10px] font-mono text-slate-300 hover:text-white border border-white/[0.12] px-2 py-1 rounded transition-colors">
              {tier}
            </button>
          )}
          <button onClick={onRefresh} className="text-[10px] font-mono text-slate-300 hover:text-white border border-white/[0.12] px-2 py-1 rounded transition-colors inline-flex items-center gap-1.5">
            <RefreshCw size={10} className={loading ? 'animate-spin' : ''} />{!isBasic && ' sync'}
          </button>
        </div>
      </div>

      {/* Context anchor -- Where am I? What can I do? How did I get here? */}
      <ContextAnchor
        capability={capability}
        agentState={agentState}
        selectedTargets={selectedTargets}
        breadcrumbs={breadcrumbs}
        allowedCaps={allowedCaps}
        stats={stats}
      />

      {/* 3-column layout -- each column scrolls independently */}
      <div className="flex-1 flex min-h-0">
        {/* Column 2: Thought stream (center) */}
        <div className="flex-1 flex flex-col min-w-0 overflow-y-auto scrollbar-thin">
          <div className="px-4 md:px-5 py-4">
            {/* Mobile-only: Collapsible context panels */}
            <div className="lg:hidden mb-4 space-y-3">
              <MobileContextPanel title="Corpus State" defaultOpen>
                <div className="grid grid-cols-2 gap-2">
                  <CorpusStat label="total" value={stats.total} />
                  <CorpusStat label="review" value={stats.inReview} tone="amber" />
                  <CorpusStat label="approved" value={stats.approved} tone="green" />
                  <CorpusStat label="minutes" value={stats.durationMinutes} />
                </div>
              </MobileContextPanel>
              {!isBasic && (
                <MobileContextPanel title="Working Memory">
                  {selectedTargets.length > 0 ? (
                    <ul className="space-y-0.5">
                      {selectedTargets.slice(0, 10).map((id) => {
                        const t = tracks.find(tr => tr.id === id);
                        return (
                          <li key={id} className="flex items-center gap-2 text-[10px] py-1 text-slate-300 font-mono">
                            <StatusDot status={t?.status} />
                            <span className="text-slate-400">{id.slice(0, 6)}</span>
                            <span className="text-slate-200 truncate">{t?.title || '?'}</span>
                          </li>
                        );
                      })}
                      {selectedTargets.length > 10 && (
                        <li className="text-[10px] text-slate-400 py-1 font-mono">...+{selectedTargets.length - 10} more</li>
                      )}
                    </ul>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {['all', 'review', 'draft', 'approved'].map((f) => (
                        <button
                          key={f}
                          onClick={() => parseCommand(`select ${f}`)}
                          className="px-2 py-1 text-[9px] font-mono text-[#00FFFF]/80 border border-[#00FFFF]/20 rounded hover:bg-[#00FFFF]/10 transition-all"
                        >
                          select {f}
                        </button>
                      ))}
                    </div>
                  )}
                </MobileContextPanel>
              )}
              <MobileContextPanel title="Actions">
                <div className="grid grid-cols-2 gap-1.5">
                  {allowedCaps.map((c) => (
                    <button
                      key={c.opcode}
                      onClick={() => {
                        if (selectedTargets.length === 0) {
                          richError({ what: `Cannot execute ${c.name}: no targets`, why: 'Requires targets', how: 'Run "select review" first', alts: ['select review', 'select all'] });
                          return;
                        }
                        addThought('reasoning', `Planning: ${c.name} on ${selectedTargets.length} targets.`);
                        void executeCapability(c);
                      }}
                      className="flex items-center gap-2 px-2.5 py-2 rounded text-[10px] text-slate-300 hover:text-white bg-white/[0.03] hover:bg-[#00FFFF]/[0.06] border border-white/[0.06] transition-colors"
                    >
                      <span className="text-[#00FFFF]/60">{c.icon}</span>
                      <span className="font-mono truncate">{c.friendlyName}</span>
                    </button>
                  ))}
                </div>
              </MobileContextPanel>
              {recentJobs.length > 0 && (
                <MobileContextPanel title={`Execution Log${runningCount > 0 ? ` (${runningCount} active)` : ''}`}>
                  <div className="space-y-1.5">
                    {recentJobs.slice(0, 5).map((j) => (
                      <div key={j.id} className="text-[10px] font-mono">
                        <div className="flex items-center gap-2">
                          <JobDot status={j.status} />
                          <span className="text-slate-200 truncate">{j.op}</span>
                        </div>
                        <div className="ml-4 text-slate-400">
                          n={j.targets.length} {new Date(j.created_at).toLocaleTimeString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </MobileContextPanel>
              )}
            </div>

            {thoughts.length === 0 ? (
              <EmptyState capability={capability} />
            ) : (
              <div className="space-y-1">
                {thoughts.map((t) => (
                  <ThoughtRow key={t.id} entry={t} capability={capability} tier={tier} />
                ))}
                <div ref={thoughtsEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Column 3: Context sidebar (lg+) -- independently scrollable */}
        {!isBasic && (
          <aside className="hidden lg:flex w-[320px] border-l border-white/[0.08] flex-col bg-[#050810] overflow-y-auto scrollbar-thin">
            {/* Working memory */}
            <div className="border-b border-white/[0.08]">
              <button
                onClick={() => setContextExpanded(!contextExpanded)}
                className="w-full px-4 py-2.5 flex items-center justify-between text-[10px] tracking-[0.12em] uppercase text-[#00FFFF] hover:text-white transition-colors"
              >
                <span className="flex items-center gap-2 font-semibold">
                  <Eye size={11} /> Working Memory
                </span>
                <span className="flex items-center gap-2">
                  <span className="font-mono normal-case tracking-normal text-[#00FFFF]/70">{selectedTargets.length} targets</span>
                  {contextExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                </span>
              </button>
              {contextExpanded && selectedTargets.length > 0 && (
                <div className="px-4 pb-3 max-h-[200px] overflow-y-auto scrollbar-thin">
                  <ul className="space-y-0.5">
                    {selectedTargets.slice(0, 20).map((id) => {
                      const t = tracks.find(tr => tr.id === id);
                      return (
                        <li key={id} className="flex items-center gap-2 text-[10px] py-1 text-slate-300 font-mono">
                          <StatusDot status={t?.status} />
                          <span className="text-slate-400">{id.slice(0, 6)}</span>
                          <span className="text-slate-200 truncate">{t?.title || '?'}</span>
                        </li>
                      );
                    })}
                    {selectedTargets.length > 20 && (
                      <li className="text-[10px] text-slate-400 py-1 font-mono">...+{selectedTargets.length - 20} more</li>
                    )}
                  </ul>
                </div>
              )}
              {contextExpanded && selectedTargets.length === 0 && (
                <div className="px-4 pb-3">
                  <div className="text-[10px] text-slate-400 font-mono py-2 mb-2">No targets selected.</div>
                  <div className="flex flex-wrap gap-1.5">
                    {['all', 'review', 'draft', 'approved'].map((f) => (
                      <button
                        key={f}
                        onClick={() => parseCommand(`select ${f}`)}
                        className="px-2 py-1 text-[9px] font-mono text-[#00FFFF]/80 border border-[#00FFFF]/20 rounded hover:bg-[#00FFFF]/10 hover:border-[#00FFFF]/40 transition-all"
                      >
                        select {f}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Stem Model */}
            <div className="px-4 py-3 border-b border-white/[0.08]">
              <div className="text-[10px] tracking-[0.12em] uppercase text-[#00FFFF] font-semibold mb-2">Choose Stem Output Model</div>
              <ModelSelectorInline currentModel={stemModel} onOpenFull={() => setShowModelSelector(true)} />
            </div>

            {/* Corpus summary */}
            <div className="px-4 py-3 border-b border-white/[0.08] space-y-2">
              <div className="text-[10px] tracking-[0.12em] uppercase text-[#00FFFF] font-semibold">Corpus State</div>
              <div className="grid grid-cols-2 gap-2">
                <CorpusStat label="total" value={stats.total} />
                <CorpusStat label="review" value={stats.inReview} tone="amber" />
                <CorpusStat label="approved" value={stats.approved} tone="green" />
                <CorpusStat label="minutes" value={stats.durationMinutes} />
              </div>
            </div>

            {/* Active Plan */}
            {activePlan && (
              <div className="px-4 py-3 border-b border-white/[0.08]">
                <div className="text-[10px] tracking-[0.12em] uppercase text-slate-300 font-semibold mb-2 flex items-center gap-2">
                  <GitBranch size={10} className="text-blue-300" /> Plan
                </div>
                <div className="text-[9px] font-mono text-slate-200 mb-1.5 truncate">{activePlan.goal}</div>
                <div className="space-y-0.5">
                  {activePlan.steps.map((step) => (
                    <div key={step.id} className="flex items-start gap-2 text-[9px] font-mono">
                      <span className={`mt-0.5 shrink-0 ${step.state === 'done' ? 'text-emerald-300' : step.state === 'running' ? 'text-blue-300 animate-pulse' : step.state === 'failed' ? 'text-red-300' : 'text-slate-500'}`}>
                        {step.state === 'done' ? '[x]' : step.state === 'running' ? '[>]' : step.state === 'failed' ? '[!]' : '[ ]'}
                      </span>
                      <div className="min-w-0">
                        <span className="text-slate-300">{step.action}</span>
                        {step.reasoning && <div className="text-slate-500 truncate">{step.reasoning}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="px-4 py-3 border-b border-white/[0.08]">
              <div className="text-[10px] tracking-[0.12em] uppercase text-[#00FFFF] font-semibold mb-2">Actions</div>
              <div className="space-y-0.5">
                {allowedCaps.map((c) => (
                  <button
                    key={c.opcode}
                    onClick={() => {
                      if (selectedTargets.length === 0) {
                        richError({
                          what: `Cannot execute ${c.name}: no targets`,
                          why: 'This action requires at least one track in the target set',
                          how: 'Run "select review" or "select all" first',
                          alts: ['select review', 'select all'],
                        });
                        return;
                      }
                      addThought('reasoning', `Planning: ${c.name} on ${selectedTargets.length} targets.`);
                      void executeCapability(c);
                    }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[10px] text-slate-300 hover:text-white hover:bg-[#00FFFF]/[0.06] transition-colors group"
                  >
                    <span className="text-[#00FFFF]/60 group-hover:text-[#00FFFF] transition-colors">{c.icon}</span>
                    <span className="font-mono flex-1 text-left truncate">{c.name}</span>
                    {isExpert && <span className="text-slate-400 text-[9px]">{c.opcode}</span>}
                    <ConsentDot consent={c.consent} />
                  </button>
                ))}
                {lockedCaps.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-white/[0.06]">
                    <div className="text-[9px] text-slate-400 uppercase tracking-[0.14em] mb-1 flex items-center gap-1.5">
                      <Lock size={8} /> Locked
                    </div>
                    {lockedCaps.map((c) => (
                      <div key={c.opcode} className="flex items-center gap-2 px-2 py-1 text-[10px] text-slate-400 font-mono">
                        <span className="opacity-50">{c.icon}</span>
                        <span className="truncate">{c.name}</span>
                        <span className="ml-auto text-[9px] text-slate-400">{c.layer}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Execution log */}
            <div className="px-4 py-3">
              <div className="text-[10px] tracking-[0.12em] uppercase text-[#00FFFF] font-semibold mb-2">
                Execution Log
                {runningCount > 0 && <span className="ml-2 text-[#39FF14]">{runningCount} active</span>}
              </div>
              {recentJobs.length === 0 ? (
                <div className="text-[10px] text-slate-400 font-mono py-2">No executions yet.</div>
              ) : (
                <div className="space-y-1.5">
                  {recentJobs.map((j) => (
                    <div key={j.id} className="text-[10px] font-mono">
                      <div className="flex items-center gap-2">
                        <JobDot status={j.status} />
                        <span className="text-slate-200 truncate">{j.op}</span>
                      </div>
                      <div className="ml-4 text-slate-400">
                        n={j.targets.length} {new Date(j.created_at).toLocaleTimeString()}
                        {isExpert && j.completed_at && ` | ${new Date(j.completed_at).getTime() - new Date(j.created_at).getTime()}ms`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>
        )}
      </div>

      {/* Command prompt -- anchored at viewport bottom, never scrolls */}
      <div className="shrink-0 border-t border-blue-400/[0.15] bg-[#060a12]">
        <div className="px-4 md:px-5 py-1.5 flex items-center justify-between border-b border-white/[0.06]">
          <div className="flex items-center gap-2 text-[9px] tracking-[0.14em] uppercase text-slate-300">
            <span className={`w-1.5 h-1.5 rounded-full ${agentState === 'idle' ? 'bg-blue-400' : agentState === 'awaiting_human' ? 'bg-amber-400 animate-pulse' : 'bg-blue-400 animate-pulse'}`} />
            {isBasic ? 'Command' : `prompt [${agentState}]`}
          </div>
          {selectedTargets.length > 0 && (
            <span className="text-[9px] font-mono text-blue-200">{selectedTargets.length} targeted</span>
          )}
        </div>
        <form onSubmit={handleCommandSubmit} className="px-4 md:px-5 py-3 flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-blue-300 shrink-0">
            <ArrowRight size={12} />
          </div>
          <input
            type="text"
            value={commandInput}
            onChange={(e) => setCommandInput(e.target.value)}
            placeholder={isBasic ? 'Type a command... try "help"' : 'NL, commands, pipelines, function calls...'}
            className="flex-1 bg-transparent border-none outline-none text-[13px] text-slate-100 placeholder:text-slate-400 font-mono"
            autoFocus
          />
          <button type="submit" className="text-slate-300 hover:text-blue-200 transition-colors p-1.5 rounded border border-white/[0.1] hover:border-blue-400/40">
            <Send size={13} />
          </button>
        </form>
      </div>

      {/* Consent overlay */}
      {pendingOp && (
        <ConsentOverlay
          op={pendingOp}
          targetCount={selectedTargets.length}
          simple={isBasic}
          onCancel={() => {
            setPendingOp(null);
            setAgentState('idle');
            addThought('result', isBasic ? 'Cancelled.' : 'Consent denied by human. Operation cancelled.');
          }}
          onConfirm={() => {
            addThought('result', isBasic ? 'Confirmed. Processing...' : 'Consent granted. Proceeding.');
            void executeCapability(pendingOp, true);
          }}
        />
      )}

      {showModelSelector && (
        <ModelSelector
          currentModel={stemModel}
          onSelect={(id) => {
            setStemModel(id);
            setStoredModelPref(id);
            const m = getModel(id);
            addThought('action', `Model switched to ${m?.name ?? id}.`, isExpert ? `arch=${m?.architecture} | rope=${m?.rotary_embedding} | stems=[${m?.stems.join(',')}]` : undefined);
            pushBreadcrumb(`model=${id}`, 'config');
          }}
          onClose={() => setShowModelSelector(false)}
        />
      )}
    </div>
  );
}

function ContextAnchor({
  capability,
  agentState,
  selectedTargets,
  breadcrumbs,
  allowedCaps,
  stats,
}: {
  capability: AgentCapability;
  agentState: AgentState;
  selectedTargets: string[];
  breadcrumbs: BreadcrumbEntry[];
  allowedCaps: CapabilityDef[];
  stats: Stats;
}) {
  const isBasic = capability === 'basic';

  const situationLabel = (() => {
    if (agentState === 'awaiting_human') return 'Waiting for your decision';
    if (agentState === 'executing') return 'Processing...';
    if (selectedTargets.length > 0) return `${selectedTargets.length} tracks targeted`;
    if (stats.inReview > 0) return `${stats.inReview} tracks need review`;
    return 'No selection';
  })();

  const nextHint = (() => {
    if (agentState === 'awaiting_human') return 'Confirm or deny the pending action';
    if (selectedTargets.length > 0) {
      const actions = allowedCaps.slice(0, 3).map(c => isBasic ? c.friendlyName.toLowerCase() : c.name.split('.').pop());
      return `Next: ${actions.join(', ')}`;
    }
    return isBasic ? 'Start with "select review" or "status"' : 'select <filter> to target tracks';
  })();

  const kindColors: Record<BreadcrumbEntry['kind'], string> = {
    select: 'text-blue-200',
    execute: 'text-emerald-200',
    navigate: 'text-slate-200',
    config: 'text-amber-200',
  };

  return (
    <div className="px-5 py-1.5 border-b border-white/[0.06] bg-[#070c16] flex items-center gap-4 text-[10px] overflow-hidden">
      {/* Where am I */}
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="text-slate-400 uppercase tracking-[0.1em] font-semibold text-[8px]">ctx</span>
        <span className="text-slate-200">{situationLabel}</span>
      </div>

      <span className="text-white/[0.15]">|</span>

      {/* What can I do */}
      <div className="text-slate-300 truncate shrink hidden sm:block">
        {nextHint}
      </div>

      {/* How did I get here -- breadcrumb trail */}
      {breadcrumbs.length > 0 && (
        <>
          <span className="text-white/[0.15] hidden md:block">|</span>
          <div className="hidden md:flex items-center gap-1 overflow-hidden flex-1 min-w-0">
            {breadcrumbs.slice(-4).map((b, i) => (
              <span key={b.id} className={`font-mono whitespace-nowrap ${kindColors[b.kind]}`}>
                {i > 0 && <span className="text-slate-400 mx-0.5">&rsaquo;</span>}
                {b.label}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function EmptyState({ capability }: { capability: AgentCapability }) {
  if (capability === 'basic') {
    return (
      <div className="flex items-center justify-center py-4 md:py-8">
        <div className="text-center max-w-sm">
          <div className="w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-3 beacon">
            <div className="beacon__ring" />
            <div className="beacon__ring" />
            <div className="beacon__ring" />
            <div className="beacon__core" />
          </div>
          <div className="text-sm text-slate-300 mb-1.5 font-medium">What would you like to do?</div>
          <div className="inline-flex flex-col gap-1 text-left text-[11px] bg-white/[0.02] border border-white/[0.06] rounded-lg px-3 py-2">
            <span className="text-slate-400"><span className="text-blue-300">select review</span> - pick tracks</span>
            <span className="text-slate-400"><span className="text-blue-300">approve</span> - approve selected</span>
            <span className="text-slate-400"><span className="text-blue-300">status</span> - catalog summary</span>
            <span className="text-slate-400"><span className="text-blue-300">help</span> - all commands</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center py-4 md:py-8">
      <div className="text-center max-w-lg">
        <div className="w-9 h-9 mx-auto rounded-full flex items-center justify-center mb-2.5 beacon">
          <div className="beacon__ring" />
          <div className="beacon__ring" />
          <div className="beacon__ring" />
          <div className="beacon__core" />
        </div>
        <div className="text-[12px] text-[#FFF] uppercase tracking-wide font-semibold mb-1.5">Agent Ready</div>
        <div className="text-[10px] text-slate-400 leading-relaxed font-mono mb-3">
          layer={capability} | protocol=mediablox-agent/1.0
          {capability === 'expert' && <><br />Full opcode surface + function-call protocol unlocked.</>}
        </div>
        <div className="inline-grid grid-cols-2 gap-x-4 gap-y-1 text-left text-[9px] font-mono bg-white/[0.02] border border-white/[0.06] rounded-lg px-4 py-2.5">
          <span className="text-slate-400"><span className="text-blue-200">NL</span> "approve all tracks in review"</span>
          <span className="text-slate-400"><span className="text-emerald-200">FN</span> catalog.select(filter=review)</span>
          <span className="text-slate-400"><span className="text-blue-200">PIPE</span> select review | approve | export</span>
          <span className="text-slate-400"><span className="text-emerald-200">JSON</span> {`{"name":"catalog.approve"}`}</span>
          <span className="text-slate-400"><span className="text-amber-200">PLAN</span> plan review and approve everything</span>
          <span className="text-slate-400"><span className="text-amber-200">MEM</span> remember batch_size=50</span>
        </div>
      </div>
    </div>
  );
}

function ThoughtRow({ entry, capability, tier }: { entry: ThoughtEntry; capability: AgentCapability; tier: AgentTier }) {
  const config: Record<ThoughtEntry['type'], { color: string; prefix: string; friendlyPrefix: string; border: string; minIcon: string }> = {
    observation: { color: 'text-slate-300', prefix: 'OBS', friendlyPrefix: 'Noticed', border: 'border-slate-400/30', minIcon: '~' },
    reasoning: { color: 'text-blue-200', prefix: 'THINK', friendlyPrefix: 'Thinking', border: 'border-blue-300/30', minIcon: '?' },
    action: { color: 'text-emerald-200', prefix: 'ACT', friendlyPrefix: 'Action', border: 'border-emerald-300/30', minIcon: '>' },
    result: { color: 'text-slate-100', prefix: 'OUT', friendlyPrefix: 'Result', border: 'border-slate-300/25', minIcon: '=' },
    error: { color: 'text-red-200', prefix: 'ERR', friendlyPrefix: 'Error', border: 'border-red-300/30', minIcon: '!' },
    handoff: { color: 'text-amber-200', prefix: 'HUMAN', friendlyPrefix: 'Confirm', border: 'border-amber-300/35', minIcon: '*' },
  };
  const c = config[entry.type];
  const isBasic = capability === 'basic';

  if (tier === 'minimal') {
    const truncated = entry.content.split('\n')[0].slice(0, 120);
    return (
      <div className={`flex items-baseline gap-2 py-0.5 text-[10px] font-mono ${entry.type === 'error' ? 'text-red-200' : entry.type === 'handoff' ? 'text-amber-200' : 'text-slate-300'}`}>
        <span className={`${c.color} opacity-70 select-none`}>{c.minIcon}</span>
        <span className="truncate">{truncated}</span>
      </div>
    );
  }

  const time = new Date(entry.ts).toLocaleTimeString([], tier === 'verbose'
    ? { hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 2 } as Intl.DateTimeFormatOptions
    : { hour: '2-digit', minute: '2-digit', second: '2-digit' }
  );
  const showDetail = entry.detail && (capability === 'expert' || tier === 'verbose');

  return (
    <div className={`flex gap-3 py-1.5 pl-3 border-l-2 ${c.border}`}>
      <div className="shrink-0 flex items-baseline gap-2" style={{ width: isBasic ? '60px' : '90px' }}>
        <span className={`text-[9px] font-bold tracking-[0.12em] uppercase ${c.color}`}>
          {isBasic ? c.friendlyPrefix : c.prefix}
        </span>
        {!isBasic && <span className="text-[9px] font-mono text-slate-400">{time}</span>}
      </div>
      <div className="min-w-0 flex-1">
        <div className={`text-[11px] leading-relaxed font-mono whitespace-pre-wrap ${entry.type === 'error' ? 'text-red-100' : entry.type === 'handoff' ? 'text-amber-100' : 'text-slate-200'}`}>
          {entry.content}
        </div>
        {showDetail && (
          <div className="text-[9px] font-mono text-slate-400 mt-0.5">{entry.detail}</div>
        )}
        {tier === 'verbose' && entry.meta && (
          <div className="text-[8px] font-mono text-slate-400 mt-0.5 px-1.5 py-0.5 bg-white/[0.03] rounded inline-block">{entry.meta}</div>
        )}
      </div>
    </div>
  );
}

function AgentStatePill({ state, simple }: { state: AgentState; simple?: boolean }) {
  const config: Record<AgentState, { label: string; simpleLabel: string; dot: string; text: string }> = {
    idle: { label: 'IDLE', simpleLabel: 'Ready', dot: 'bg-blue-400', text: 'text-blue-300' },
    planning: { label: 'PLANNING', simpleLabel: 'Thinking...', dot: 'bg-purple-400 animate-pulse', text: 'text-purple-300' },
    executing: { label: 'EXECUTING', simpleLabel: 'Working...', dot: 'bg-emerald-400 animate-pulse', text: 'text-emerald-300' },
    awaiting_human: { label: 'AWAITING', simpleLabel: 'Need your input', dot: 'bg-amber-400 animate-pulse', text: 'text-amber-300' },
    observing: { label: 'OBSERVING', simpleLabel: 'Done', dot: 'bg-blue-300 animate-pulse', text: 'text-blue-200' },
    blocked: { label: 'BLOCKED', simpleLabel: 'Stuck', dot: 'bg-red-400', text: 'text-red-300' },
  };
  const c = config[state];
  return (
    <div className="flex items-center gap-2">
      <span className={`w-2 h-2 rounded-full ${c.dot}`} />
      <span className={`text-[10px] font-bold tracking-[0.14em] uppercase ${c.text}`}>
        {simple ? c.simpleLabel : c.label}
      </span>
    </div>
  );
}

function LayerSwitcher({ capability, onChange }: { capability: AgentCapability; onChange: (c: AgentCapability) => void }) {
  const layers: AgentCapability[] = ['basic', 'advanced', 'expert'];
  const idx = layers.indexOf(capability);
  const colors: Record<AgentCapability, string> = {
    basic: 'text-blue-300 border-blue-400/30',
    advanced: 'text-cyan-300 border-cyan-400/30',
    expert: 'text-amber-300 border-amber-400/30',
  };

  return (
    <button
      onClick={() => onChange(layers[(idx + 1) % layers.length])}
      className={`text-[10px] font-mono border px-2 py-1 rounded transition-all inline-flex items-center gap-1.5 hover:brightness-125 ${colors[capability]}`}
    >
      {capability === 'basic' && <ChevronUp size={9} />}
      {capability === 'advanced' && <ChevronUp size={9} />}
      {capability === 'expert' && <ChevronDown size={9} />}
      {capability}
    </button>
  );
}

function StatusDot({ status }: { status?: TrackStatus }) {
  const cls = status === 'approved' ? 'bg-emerald-400' : status === 'in_review' ? 'bg-amber-400' : status === 'published' ? 'bg-blue-400' : 'bg-slate-500';
  return <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cls}`} />;
}

function CorpusStat({ label, value, tone }: { label: string; value: number; tone?: 'amber' | 'green' }) {
  const valCls = tone === 'amber' ? 'text-[#FFD600]' : tone === 'green' ? 'text-[#39FF14]' : 'text-white';
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-[10px] font-mono text-[#00FFFF]/60">{label}</span>
      <span className={`text-[12px] font-mono font-semibold tabular-nums ${valCls}`}>{value}</span>
    </div>
  );
}

function ConsentDot({ consent }: { consent: string }) {
  const cls = consent === 'explicit' ? 'bg-[#FF003C] shadow-[0_0_4px_#FF003C]' : consent === 'confirm' ? 'bg-[#FFD600] shadow-[0_0_4px_#FFD600]' : 'bg-[#39FF14] shadow-[0_0_4px_#39FF14]';
  return <span className={`w-1.5 h-1.5 rounded-full ${cls}`} />;
}

function JobDot({ status }: { status: string }) {
  const cls = status === 'succeeded' ? 'text-[#39FF14]' : status === 'failed' ? 'text-[#FF003C]' : status === 'running' ? 'text-[#00FFFF] animate-pulse' : 'text-slate-500';
  return <Square size={8} className={`fill-current ${cls}`} />;
}

function MobileContextPanel({ title, children, defaultOpen = false }: { title: string; children: ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-white/[0.06] bg-[#0a0e17]/80 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-300"
      >
        <span>{title}</span>
        <ChevronRight size={12} className={`transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}

function ConsentOverlay({
  op,
  targetCount,
  simple,
  onCancel,
  onConfirm,
}: {
  op: CapabilityDef;
  targetCount: number;
  simple?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-[#0a0f1a] border border-amber-400/30 rounded-lg w-full max-w-md p-5 shadow-[0_0_60px_rgba(245,158,11,0.05)]">
        {simple ? (
          <>
            <div className="flex items-center gap-2 text-[11px] text-amber-400 font-semibold mb-3">
              <CircleDot size={12} className="animate-pulse" />
              Confirmation needed
            </div>
            <div className="text-sm text-slate-200 mb-2">
              {op.friendlyName} {targetCount} track{targetCount !== 1 ? 's' : ''}?
            </div>
            <div className="text-[12px] text-slate-400 mb-5">{op.description}</div>
            {!op.reversible && (
              <div className="flex items-start gap-2 border border-red-400/20 bg-red-400/5 rounded px-3 py-2 text-[11px] text-red-200 mb-4">
                <AlertTriangle size={12} className="mt-[1px] shrink-0 text-red-400" />
                <span>This cannot be undone.</span>
              </div>
            )}
            <div className="flex items-center justify-end gap-3">
              <button onClick={onCancel} className="px-4 py-2 text-[12px] text-slate-400 hover:text-white border border-white/[0.08] rounded transition-colors">
                Cancel
              </button>
              <button onClick={onConfirm} className="px-4 py-2 text-[12px] font-medium rounded bg-blue-500/20 text-blue-200 border border-blue-400/40 hover:bg-blue-500/30 transition-all">
                Yes, {op.friendlyName.toLowerCase()}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-amber-400 font-bold mb-4">
              <CircleDot size={12} className="animate-pulse" />
              Human Decision Required
            </div>
            <div className="font-mono text-sm text-slate-200 mb-3">{op.name}</div>
            <div className="text-[11px] text-slate-400 mb-4">{op.description}</div>
            <div className="grid grid-cols-3 gap-3 text-[10px] font-mono mb-5 py-3 border-y border-white/[0.06]">
              <div>
                <div className="text-slate-500">targets</div>
                <div className="text-slate-200 mt-0.5">{targetCount}</div>
              </div>
              <div>
                <div className="text-slate-500">reversible</div>
                <div className={`mt-0.5 ${op.reversible ? 'text-emerald-300' : 'text-red-300'}`}>{String(op.reversible)}</div>
              </div>
              <div>
                <div className="text-slate-500">consent</div>
                <div className={`mt-0.5 ${op.consent === 'explicit' ? 'text-red-300' : 'text-amber-300'}`}>{op.consent}</div>
              </div>
            </div>
            {!op.reversible && (
              <div className="flex items-start gap-2 border border-red-400/20 bg-red-400/5 rounded px-3 py-2 text-[10px] text-red-200 mb-4">
                <AlertTriangle size={12} className="mt-[1px] shrink-0 text-red-400" />
                <span>Irreversible. Cannot be undone after confirmation.</span>
              </div>
            )}
            <div className="flex items-center justify-end gap-3">
              <button onClick={onCancel} className="px-3 py-1.5 text-[11px] font-mono text-slate-400 hover:text-white border border-white/[0.08] rounded transition-colors">
                deny
              </button>
              <button
                onClick={onConfirm}
                className={`px-4 py-1.5 text-[11px] font-mono font-semibold rounded inline-flex items-center gap-2 transition-all ${
                  op.consent === 'explicit'
                    ? 'bg-red-500/20 text-red-200 border border-red-400/40 hover:bg-red-500/30'
                    : 'bg-blue-500/20 text-blue-200 border border-blue-400/40 hover:bg-blue-500/30'
                }`}
              >
                <Play size={10} /> confirm
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function toCsv(tracks: Track[]): string {
  const headers = ['id', 'title', 'artist', 'album', 'status', 'duration_seconds', 'bpm', 'key_signature', 'audio_url', 'updated_at'];
  const lines = [headers.join(',')];
  for (const t of tracks) {
    lines.push(headers.map((h) => csvEscape(String((t as unknown as Record<string, unknown>)[h] ?? ''))).join(','));
  }
  return lines.join('\n');
}

function csvEscape(v: string): string {
  if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

function downloadFile(name: string, contents: string) {
  const blob = new Blob([contents], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
